import { useMemo } from "react";
import { UMBRALES } from "./useMapaCalorData";

// ─── Helpers ─────────────────────────────────────────────────────────────────
const getTier = (cat) => (cat === "A" || cat === "B" ? cat : "C");

const diasDesde = (fecha) => {
  if (!fecha) return Infinity;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
};

const colorFromDias = (dias, umbral) => {
  if (dias <= umbral.verde) return "verde";
  if (dias <= umbral.amarillo) return "amarillo";
  return "rojo";
};

const COLOR_PRIORIDAD = { rojo: 3, amarillo: 2, verde: 1 };

const peorColor = (colores) => {
  if (colores.length === 0) return "rojo";
  return colores.reduce((peor, c) => (COLOR_PRIORIDAD[c] > COLOR_PRIORIDAD[peor] ? c : peor), "verde");
};

// Parsear nombre de intermediario → { holding, agenciaHija }
const HOLDINGS = ["IPG", "OMNICOM", "DENTSU", "HAVAS", "PUBLICIS", "WPP", "OMG"];

const parseIntermediario = (nombre) => {
  if (!nombre) return { holding: null, agenciaHija: null };
  const parts = nombre.split("|").map((s) => s.trim());

  if (parts.length >= 2) {
    let holding = parts[0].toUpperCase();
    if (holding === "OMG") holding = "OMNICOM";
    return { holding, agenciaHija: parts.slice(1).join(" | ").trim() };
  }

  const upper = parts[0].toUpperCase();
  if (HOLDINGS.includes(upper)) return { holding: upper, agenciaHija: null };
  return { holding: null, agenciaHija: null };
};

// OMNET: equipo especial de negociación Omnicom (IDs importados de permisos.js)
export const OMNET_CARGO = "OMNET";

// Roles de contacto agencia (orden jerárquico) — 5 niveles
const ROLES_ORDER = ["Gerente", "Director", "Supervisor", "Planificador", "Asistente"];
const ROLES_SHORT = {
  Gerente: "Gerente",
  Director: "Director",
  Supervisor: "Supervisor",
  Planificador: "Planificador",
  Asistente: "Asistente",
  // Compatibilidad con cargos legacy
  "Director de cuenta": "Director",
  "Supervisor de cuenta": "Supervisor",
};

// Normalizar cargo legacy → nuevo
const normalizeCargo = (cargo) => {
  if (cargo === "Director de cuenta") return "Director";
  if (cargo === "Supervisor de cuenta") return "Supervisor";
  return cargo;
};

const getRolIndex = (cargo) => {
  const norm = normalizeCargo(cargo);
  const idx = ROLES_ORDER.indexOf(norm);
  return idx !== -1 ? idx : 99;
};

// ─── Hook ────────────────────────────────────────────────────────────────────
export const useMapaCalorAgenciasData = (
  clientes,
  usuarios,
  intermediarios,
  actividades,
  categorias,
  contactosAgencia = []
) => {
  const usuariosMap = useMemo(() => {
    const map = {};
    usuarios.forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [usuarios]);

  const intermediariosMap = useMemo(() => {
    const map = {};
    intermediarios.forEach((i) => {
      map[i.id] = i;
    });
    return map;
  }, [intermediarios]);

  const categoriasMap = useMemo(() => {
    const map = {};
    categorias.forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [categorias]);

  // Mapa: cliente_id → [{ usuario_id, cargo }] desde tabla relacional
  const contactosPorCliente = useMemo(() => {
    const map = {};
    contactosAgencia.forEach((ca) => {
      if (!map[ca.cliente_id]) map[ca.cliente_id] = [];
      map[ca.cliente_id].push(ca);
    });
    return map;
  }, [contactosAgencia]);

  const usaTablaRelacional = contactosAgencia.length > 0;

  // Gerentes resueltos directo desde usuarios (cargo="Gerente" + campo agencia → intermediario)
  // Jerarquía:
  //   - Intermediario SIN hija (ej: "HAVAS") → Gerente de Holding → aparece en TODAS las agencias del holding
  //   - Intermediario CON hija (ej: "IPG | INITIATIVE") → Gerente de Agencia → solo en esa agencia
  const { gerentesPorIntermediario, gerentesPorHolding } = useMemo(() => {
    const byInterm = {}; // intermId → [usuario enriquecido]
    const byHolding = {}; // holdingName → [usuario enriquecido] (solo gerentes de holding)
    usuarios.forEach((u) => {
      const cargoNorm = normalizeCargo(u.cargo);
      if (cargoNorm !== "Gerente" || !u.agencia) return;
      const enriched = { ...u, cargoNorm: "Gerente", rolIndex: 0, rolShort: "Gerente" };
      if (!byInterm[u.agencia]) byInterm[u.agencia] = [];
      byInterm[u.agencia].push(enriched);
      // Solo mapear al holding si el intermediario NO tiene agencia hija
      const interm = intermediariosMap[u.agencia];
      if (interm) {
        const { holding, agenciaHija } = parseIntermediario(interm.nombre);
        if (holding && !agenciaHija) {
          // Gerente de Holding: intermediario es solo el holding (ej: "HAVAS", "PUBLICIS")
          if (!byHolding[holding]) byHolding[holding] = [];
          byHolding[holding].push(enriched);
        }
      }
    });
    return { gerentesPorIntermediario: byInterm, gerentesPorHolding: byHolding };
  }, [usuarios, intermediariosMap]);

  const data = useMemo(() => {
    if (clientes.length === 0 || usuarios.length === 0) return [];

    // 1. Enriquecer cada cliente con sus contactos de agencia + calor
    const clientesEnriquecidos = clientes
      .filter((cli) => cli.agencia && intermediariosMap[cli.agencia])
      .map((cli) => {
        const interm = intermediariosMap[cli.agencia];
        if (!interm || (interm.nombre || "").toUpperCase() === "DIRECTO") return null;

        const tier = getTier(cli.categoria_cliente);
        const umbralAg = UMBRALES[tier].agencia;
        const actPerEmail = actividades[cli.id] || {};

        // Obtener contactos: preferir tabla relacional si existe, fallback a campos legacy
        // Excluir OMNET del pipeline regular (se maneja aparte)
        const ccaRows = usaTablaRelacional && contactosPorCliente[cli.id] ? contactosPorCliente[cli.id] : [];
        const regularCca = ccaRows.filter((ca) => ca.cargo !== OMNET_CARGO);
        const contactoIds =
          regularCca.length > 0
            ? regularCca.map((ca) => ({ id: ca.usuario_id, cargoOverride: ca.cargo }))
            : [
                cli.contacto_agencia && { id: cli.contacto_agencia, cargoOverride: null },
                cli.contacto_agencia_1 && { id: cli.contacto_agencia_1, cargoOverride: null },
                cli.contacto_agencia_2 && { id: cli.contacto_agencia_2, cargoOverride: null },
                cli.contacto_agencia_3 && { id: cli.contacto_agencia_3, cargoOverride: null },
              ].filter(Boolean);

        // OMNET: contacto especial para Omnicom
        const omnetEntry = ccaRows.find((ca) => ca.cargo === OMNET_CARGO);
        const omnetUser = omnetEntry ? usuariosMap[omnetEntry.usuario_id] : null;

        const contactos = contactoIds
          .map(({ id, cargoOverride }) => usuariosMap[id] && { user: usuariosMap[id], cargoOverride })
          .filter(Boolean)
          .map(({ user, cargoOverride }) => {
            const email = user.email?.toLowerCase();
            const lastDate = email ? actPerEmail[email] || null : null;
            const dias = diasDesde(lastDate);
            const cargo = cargoOverride || user.cargo;
            const cargoNorm = normalizeCargo(cargo);
            return {
              ...user,
              dias,
              color: colorFromDias(dias, umbralAg),
              lastDate,
              cargoNorm,
              rolIndex: getRolIndex(cargo),
              rolShort: ROLES_SHORT[cargo] || cargo || "—",
            };
          })
          .sort((a, b) => a.rolIndex - b.rolIndex);

        const allAgColors = contactos.map((c) => c.color);
        const colorAgencia = contactos.length === 0 ? "rojo" : peorColor(allAgColors);

        // Separar: Director, resto del equipo (Gerentes se resuelven a nivel agencia, no per-cliente)
        const director =
          contactos.find((c) => c.cargoNorm === "Director") ||
          contactos.find((c) => c.cargo === "Director de cuenta") ||
          null;
        const resto = contactos.filter((c) => c.cargoNorm !== "Gerente" && c.cargoNorm !== "Director");

        // Enriquecer contacto OMNET con actividad y color
        let omnetContacto = null;
        if (omnetUser) {
          const omnetEmail = omnetUser.email?.toLowerCase();
          const omnetLastDate = omnetEmail ? actPerEmail[omnetEmail] || null : null;
          const omnetDias = diasDesde(omnetLastDate);
          omnetContacto = {
            ...omnetUser,
            dias: omnetDias,
            color: colorFromDias(omnetDias, umbralAg),
            lastDate: omnetLastDate,
          };
        }

        return {
          ...cli,
          intermNombre: interm.nombre,
          contactosAgencia: contactos,
          director,
          equipoSinDirector: resto,
          colorAgencia,
          categoriaNombre: categoriasMap[cli.categoria]?.nombre || "",
          omnetContacto,
        };
      })
      .filter(Boolean);

    // 2. Agrupar: Holding → Agencia hija → Equipo exacto (director+sup+plan+asis) → Clientes
    const holdingsMap = {};

    clientesEnriquecidos.forEach((cli) => {
      const { holding, agenciaHija } = parseIntermediario(cli.intermNombre);
      const holdingKey = holding || cli.intermNombre;
      const hijaKey = agenciaHija || cli.intermNombre;

      // Clave de equipo = combinación exacta de IDs de todos los contactos no-gerente
      const dirId = cli.director ? String(cli.director.id) : "0";
      const miembroIds = cli.equipoSinDirector.map((m) => String(m.id)).sort();
      const teamKey = [dirId, ...miembroIds].join("-");

      if (!holdingsMap[holdingKey]) {
        holdingsMap[holdingKey] = { nombre: holdingKey, agencias: {} };
      }
      if (!holdingsMap[holdingKey].agencias[hijaKey]) {
        holdingsMap[holdingKey].agencias[hijaKey] = {
          nombre: hijaKey,
          equipos: {},
          gerentes: {},
          intermNombres: new Set(),
        };
      }
      const agencia = holdingsMap[holdingKey].agencias[hijaKey];
      agencia.intermNombres.add(cli.intermNombre);
      // Registrar intermId para inyectar gerentes después
      if (!agencia.intermIds) agencia.intermIds = new Set();
      agencia.intermIds.add(cli.agencia);

      if (!agencia.equipos[teamKey]) {
        agencia.equipos[teamKey] = {
          director: cli.director,
          miembros: {},
          clientes: [],
        };
      }
      const equipo = agencia.equipos[teamKey];
      equipo.clientes.push(cli);
      cli.equipoSinDirector.forEach((m) => {
        if (!equipo.miembros[m.id]) equipo.miembros[m.id] = m;
      });
    });

    // 2b. Inyectar gerentes desde usuarios
    // Helper: enriquecer gerente con actividad y color
    const enrichGerente = (g, intermIds) => {
      const email = g.email?.toLowerCase();
      let lastDate = null;
      intermIds.forEach((iid) => {
        clientes
          .filter((c) => c.agencia === iid)
          .forEach((c) => {
            const act = actividades[c.id];
            if (act && act[email] && (!lastDate || new Date(act[email]) > new Date(lastDate))) {
              lastDate = act[email];
            }
          });
      });
      const dias = diasDesde(lastDate);
      const umbralAg = UMBRALES.B?.agencia || UMBRALES.C?.agencia || { verde: 14, amarillo: 30 };
      return { ...g, dias, color: colorFromDias(dias, umbralAg), lastDate };
    };

    // Recopilar todos los intermIds por holding (para buscar actividad + usuarios)
    const holdingIntermIds = {};
    Object.entries(holdingsMap).forEach(([hKey, h]) => {
      const ids = new Set();
      Object.values(h.agencias).forEach((ag) => (ag.intermIds || []).forEach((id) => ids.add(id)));
      holdingIntermIds[hKey] = ids;
    });
    // Incluir intermediarios holding-level (sin agencia hija) para capturar usuarios vinculados al holding
    Object.entries(intermediariosMap).forEach(([intermId, interm]) => {
      const { holding, agenciaHija } = parseIntermediario(interm.nombre);
      if (holding && !agenciaHija && holdingIntermIds[holding]) {
        holdingIntermIds[holding].add(Number(intermId));
      }
    });

    Object.entries(holdingsMap).forEach(([hKey, h]) => {
      const holdingGerentesRaw = gerentesPorHolding[hKey] || [];
      const allIntermIds = holdingIntermIds[hKey] || new Set();

      // Gerentes de holding → se muestran a nivel holding, NO en cada agencia hija
      if (!h.gerentes) h.gerentes = {};
      holdingGerentesRaw.forEach((g) => {
        if (!h.gerentes[g.id]) h.gerentes[g.id] = enrichGerente(g, allIntermIds);
      });

      // Gerentes específicos de cada agencia (por intermId)
      Object.values(h.agencias).forEach((ag) => {
        (ag.intermIds || []).forEach((intermId) => {
          (gerentesPorIntermediario[intermId] || []).forEach((g) => {
            // No duplicar si ya es gerente de holding
            if (!h.gerentes[g.id] && !ag.gerentes[g.id]) {
              ag.gerentes[g.id] = enrichGerente(g, ag.intermIds);
            }
          });
        });
      });
    });

    // 3. Construir estructura final
    const resultado = Object.values(holdingsMap)
      .map((h) => {
        const agencias = Object.values(h.agencias)
          .map((ag) => {
            const gerentesArr = Object.values(ag.gerentes).sort((a, b) =>
              `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`)
            );

            const equipos = Object.values(ag.equipos)
              .map((eq) => {
                const miembrosArr = Object.values(eq.miembros).sort((a, b) => a.rolIndex - b.rolIndex);
                const supervisores = miembrosArr.filter((m) => m.cargoNorm === "Supervisor");
                const planificadores = miembrosArr.filter((m) => m.cargoNorm === "Planificador");
                const asistentes = miembrosArr.filter((m) => m.cargoNorm === "Asistente");
                const clientesOrd = eq.clientes.sort((a, b) => (a.empresa || "").localeCompare(b.empresa || ""));
                const colores = clientesOrd.map((c) => c.colorAgencia);
                const dirColor = eq.director ? eq.director.color : "rojo";
                const allColors = [dirColor, ...miembrosArr.map((m) => m.color)];

                return {
                  director: eq.director,
                  miembros: miembrosArr,
                  supervisores,
                  planificadores,
                  asistentes,
                  clientes: clientesOrd,
                  color: peorColor(colores),
                  colorEquipo: peorColor(allColors),
                };
              })
              .sort((a, b) => {
                // Equipos con Director primero, sin Director al final
                if (a.director && !b.director) return -1;
                if (!a.director && b.director) return 1;
                const nameA = a.director ? `${a.director.nombre} ${a.director.apellido}` : "";
                const nameB = b.director ? `${b.director.nombre} ${b.director.apellido}` : "";
                const dirCmp = nameA.localeCompare(nameB);
                if (dirCmp !== 0) return dirCmp;
                // Secundario: agrupar por Supervisor → Planificador → Asistente
                const supA = a.supervisores[0] ? `${a.supervisores[0].nombre} ${a.supervisores[0].apellido}` : "zzz";
                const supB = b.supervisores[0] ? `${b.supervisores[0].nombre} ${b.supervisores[0].apellido}` : "zzz";
                const supCmp = supA.localeCompare(supB);
                if (supCmp !== 0) return supCmp;
                const planA = a.planificadores[0]
                  ? `${a.planificadores[0].nombre} ${a.planificadores[0].apellido}`
                  : "zzz";
                const planB = b.planificadores[0]
                  ? `${b.planificadores[0].nombre} ${b.planificadores[0].apellido}`
                  : "zzz";
                return planA.localeCompare(planB);
              });

            const allColors = equipos.map((e) => e.color);
            const totalClientes = equipos.reduce((s, e) => s + e.clientes.length, 0);

            return {
              nombre: ag.nombre,
              equipos,
              gerentes: gerentesArr,
              color: peorColor(allColors),
              totalClientes,
              intermNombres: [...ag.intermNombres],
              intermIds: [...(ag.intermIds || [])],
            };
          })
          .sort((a, b) => a.nombre.localeCompare(b.nombre));

        const coloresAgencias = agencias.map((a) => a.color);
        const holdingGerentesArr = Object.values(h.gerentes || {}).sort((a, b) =>
          `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`)
        );
        return {
          ...h,
          agencias,
          gerentes: holdingGerentesArr,
          color: peorColor(coloresAgencias),
          totalClientes: agencias.reduce((sum, a) => sum + a.totalClientes, 0),
          holdingIntermIds: [...(holdingIntermIds[h.nombre] || [])],
        };
      })
      .sort((a, b) => {
        const aIsHolding = HOLDINGS.includes(a.nombre.toUpperCase()) ? 0 : 1;
        const bIsHolding = HOLDINGS.includes(b.nombre.toUpperCase()) ? 0 : 1;
        if (aIsHolding !== bIsHolding) return aIsHolding - bIsHolding;
        return a.nombre.localeCompare(b.nombre);
      });

    return resultado;
  }, [
    clientes,
    usuarios,
    actividades,
    intermediariosMap,
    usuariosMap,
    categoriasMap,
    contactosPorCliente,
    usaTablaRelacional,
    gerentesPorIntermediario,
    gerentesPorHolding,
  ]);

  // Mapa de usuarios por intermediario ID (para dropdowns en modo edición)
  const usuariosPorAgencia = useMemo(() => {
    if (clientes.length === 0 || usuarios.length === 0) return {};
    const map = {}; // intermId → { userId → usuario }

    // 1. Usuarios asignados a clientes (tabla relacional o legacy)
    clientes
      .filter((cli) => cli.agencia && intermediariosMap[cli.agencia])
      .forEach((cli) => {
        const interm = intermediariosMap[cli.agencia];
        if (!interm || (interm.nombre || "").toUpperCase() === "DIRECTO") return;
        const intermId = cli.agencia;
        if (!map[intermId]) map[intermId] = {};
        const ids =
          usaTablaRelacional && contactosPorCliente[cli.id]
            ? contactosPorCliente[cli.id].map((ca) => ca.usuario_id)
            : [cli.contacto_agencia, cli.contacto_agencia_1, cli.contacto_agencia_2, cli.contacto_agencia_3];
        ids
          .filter((id) => id != null)
          .forEach((id) => {
            const u = usuariosMap[id];
            if (u) map[intermId][u.id] = u;
          });
      });

    // 2. Usuarios por campo usuarios.agencia (incluye gerentes y cualquier persona vinculada)
    usuarios.forEach((u) => {
      if (!u.agencia) return;
      const interm = intermediariosMap[u.agencia];
      if (!interm || (interm.nombre || "").toUpperCase() === "DIRECTO") return;
      const intermId = u.agencia;
      if (!map[intermId]) map[intermId] = {};
      map[intermId][u.id] = u;
    });

    // Convertir a arrays ordenados por nombre
    const result = {};
    Object.entries(map).forEach(([intermId, usersMap]) => {
      result[intermId] = Object.values(usersMap).sort((a, b) =>
        `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`)
      );
    });
    return result;
  }, [clientes, usuarios, intermediariosMap, usuariosMap, usaTablaRelacional, contactosPorCliente]);

  return { data, usuariosPorAgencia };
};

export { ROLES_ORDER, ROLES_SHORT };
